/**
 * Filter state for the feedback list pages (My Feedback + Admin Feedback).
 *
 * State lives in the URL's search params so filtered views are bookmarkable,
 * shareable, and survive back/forward. Short keys (q, s, t, age, v, f, sev,
 * pri, by) keep the URL readable.
 *
 * `filtered` is the memoized subset of tickets matching the current state.
 * `options` is the set of values present in the data for the dynamic
 * dropdowns (App Version, Feature, Submitted By) — no static list.
 */
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { FeedbackTicket } from '../../types/feedback';
import { featureFromUrl } from './featureFromUrl';

export interface FeedbackFilterState {
  search: string;
  statuses: Set<string>;
  types: Set<string>;
  ageDays: number | null;
  versions: Set<string>;
  features: Set<string>;
  severities: Set<string>;
  priorities: Set<string>;
  submittedBy: Set<string>;
}

export interface FeedbackFilterUpdate {
  search?: string;
  statuses?: string[];
  types?: string[];
  ageDays?: number | null;
  versions?: string[];
  features?: string[];
  severities?: string[];
  priorities?: string[];
  submittedBy?: string[];
}

export interface FilterOptions {
  versions: string[];
  features: string[];
  submitters: { email: string; count: number }[];
}

const K = {
  search: 'q',
  statuses: 's',
  types: 't',
  ageDays: 'age',
  versions: 'v',
  features: 'f',
  severities: 'sev',
  priorities: 'pri',
  submittedBy: 'by',
} as const;

function getSet(sp: URLSearchParams, key: string): Set<string> {
  const v = sp.get(key);
  return v ? new Set(v.split(',').filter(Boolean)) : new Set();
}

export function useFeedbackFilters(tickets: FeedbackTicket[]) {
  const [sp, setSp] = useSearchParams();

  const state: FeedbackFilterState = useMemo(() => ({
    search: sp.get(K.search) || '',
    statuses: getSet(sp, K.statuses),
    types: getSet(sp, K.types),
    ageDays: sp.get(K.ageDays) ? Number(sp.get(K.ageDays)) : null,
    versions: getSet(sp, K.versions),
    features: getSet(sp, K.features),
    severities: getSet(sp, K.severities),
    priorities: getSet(sp, K.priorities),
    submittedBy: getSet(sp, K.submittedBy),
  }), [sp]);

  const update = (patch: FeedbackFilterUpdate) => {
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      const setStr = (key: string, val: string | undefined | null) => {
        if (val) next.set(key, val); else next.delete(key);
      };
      const setArr = (key: string, val: string[] | undefined) => {
        if (val === undefined) return;
        if (val.length) next.set(key, val.join(','));
        else next.delete(key);
      };
      if ('search' in patch) setStr(K.search, patch.search);
      setArr(K.statuses, patch.statuses);
      setArr(K.types, patch.types);
      if ('ageDays' in patch) setStr(K.ageDays, patch.ageDays ? String(patch.ageDays) : null);
      setArr(K.versions, patch.versions);
      setArr(K.features, patch.features);
      setArr(K.severities, patch.severities);
      setArr(K.priorities, patch.priorities);
      setArr(K.submittedBy, patch.submittedBy);
      return next;
    }, { replace: true });
  };

  const clear = () => setSp(new URLSearchParams(), { replace: true });

  // Dynamic dropdown options: only values that actually appear in the data.
  const options: FilterOptions = useMemo(() => {
    const versions = new Set<string>();
    const features = new Set<string>();
    const submitterCounts: Record<string, number> = {};
    tickets.forEach((t) => {
      if (t.AppVersion) versions.add(t.AppVersion);
      features.add(featureFromUrl(t.PageUrl));
      if (t.SubmittedByEmail) {
        submitterCounts[t.SubmittedByEmail] = (submitterCounts[t.SubmittedByEmail] || 0) + 1;
      }
    });
    return {
      versions: Array.from(versions).sort().reverse(), // newest versions first (lexicographic is close enough)
      features: Array.from(features).sort(),
      submitters: Object.entries(submitterCounts)
        .map(([email, count]) => ({ email, count }))
        .sort((a, b) => b.count - a.count),
    };
  }, [tickets]);

  const filtered = useMemo(() => {
    const q = state.search.trim().toLowerCase();
    const cutoff = state.ageDays ? Date.now() - state.ageDays * 86_400_000 : null;

    return tickets.filter((t) => {
      if (q) {
        const hay = [t.TicketNumber, t.Title, t.RefinedTitle, t.Description]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (state.statuses.size && !state.statuses.has(t.Status)) return false;
      if (state.types.size && !state.types.has(t.Type)) return false;
      if (state.versions.size && !state.versions.has(t.AppVersion || '')) return false;
      if (state.features.size && !state.features.has(featureFromUrl(t.PageUrl))) return false;
      if (state.severities.size && !state.severities.has(t.Severity || '')) return false;
      if (state.priorities.size && !state.priorities.has(t.Priority || '')) return false;
      if (state.submittedBy.size && !state.submittedBy.has(t.SubmittedByEmail || '')) return false;
      if (cutoff !== null) {
        const created = new Date(t.sysCreateDate).getTime();
        if (!isFinite(created) || created < cutoff) return false;
      }
      return true;
    });
  }, [tickets, state]);

  const activeCount =
    (state.search ? 1 : 0) +
    (state.statuses.size ? 1 : 0) +
    (state.types.size ? 1 : 0) +
    (state.ageDays ? 1 : 0) +
    (state.versions.size ? 1 : 0) +
    (state.features.size ? 1 : 0) +
    (state.severities.size ? 1 : 0) +
    (state.priorities.size ? 1 : 0) +
    (state.submittedBy.size ? 1 : 0);

  return { state, update, clear, options, filtered, activeCount };
}
